// src/components/FrameworkCustomizer.js
import React from "react";
import {
  Paper,
  Stack,
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LinkIcon from "@mui/icons-material/Link";
import AiBorderWrapper from "./AIBorderWrapper";
import { getCatalog } from "../api";
import WordCountTextFieldWithExample from "./WordCountTextFieldWithExample";
import { getPairedId, groupIntoPairs } from "../utils/mluPairingUtils";

// ---------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------
const BG_COLORS = {
  EDU: "#ffebee",
  GOV: "#e8f5e9",
  TEC: "#e3f2fd",
  EID: "#fff8e1",
};

const MAIN_COLORS = {
  EDU: "#c62828",
  GOV: "#2e7d32",
  TEC: "#1565c0",
  EID: "#f9a825",
};

// ---------------------------------------------------------------------
// Placeholder text (3-question guidance for EXPANDED state)
// ---------------------------------------------------------------------
const DEFAULT_NOTE_PLACEHOLDER = [
  "Click on the 'Show guiding questions' button to see a template for structuring your note",
].join("\n");

// Short, universal placeholder for COLLAPSED + empty state
const COLLAPSED_SIMPLE_PLACEHOLDER =
  "Add a short note on goals, why these MLUs, and planned follow-ups (max 100 words).";

// ---------------------------------------------------------------------
// Example + guiding questions per framework
// ---------------------------------------------------------------------
const NOTE_EXAMPLE_BY_PREFIX = {
  EDU:
    "Our learning group is going to start working with an LDT, it's important that they acquire basic knowledge therefore we accept the selection of the Agent (EDU-101 and EDU-102) and would like to add the EDU-201 and EDU-202 so that they can also anchor questions of value to the LDT.",

  GOV:
    "Our learning group needs to better understand how their data flows in mobility has to be improved. The agent selection GOV-201 and GOV-202 covers basic knowledge, but they have to achieve a higher level so that they can perform as expected, so the MLUs GOV-203 and GOV-204 should be added.",

  TEC:
    "Our learning group is already advanced in the integration of AI supported tools into the LDT; however they are missing the ethical consideration of data quality. At this stage besides the agent suggestions it is relevant to add the MLU TEC-305 to the selection.",

  EID:
    "Our learning group is aware of ethical guidelines in LDT projects but they are missing concrete tools to improve their citizen participation strategy; therefore we have deselected the agent suggestion of EID-101 and EID-102, and added the MLUs EID-301 and EID-302.",
};



const NOTE_GUIDING_TEXT = [
  "• State the main purpose of this block for your learner or team.",
  "• Explain briefly why these MLUs are a good next step (link to roles, goals, or gaps).",
  "• Mention any planned follow-up activities (workshops, internal meetings, experiments).",
  "• Keep it concrete and practical – imagine you are explaining this to a colleague.",
].join("\n");

// ---------------------------------------------------------------------
// Catalog fetch cache (shared within this module)
// ---------------------------------------------------------------------
let catalogCache = null;
let catalogPromise = null;

async function fetchCatalogOnce() {
  if (catalogCache) return catalogCache;

  if (!catalogPromise) {
    catalogPromise = getCatalog()
      .then((data) => {
        const items = data?.items || [];
        catalogCache = items;
        return catalogCache;
      })
      .catch((err) => {
        console.error("[FrameworkCustomizer] Failed to load catalog:", err);
        catalogCache = null;
        throw err;
      });
  }
  return catalogPromise;
}

// ---------------------------------------------------------------------
// Compute selection status
// ---------------------------------------------------------------------
function computeStatus(selected = [], aiSelection = []) {
  const selectedIds = Array.isArray(selected) ? selected : [];
  const aiIds = Array.isArray(aiSelection) ? aiSelection : [];

  const selectedSet = new Set(selectedIds);
  const aiSet = new Set(aiIds);

  const selectedCount = selectedIds.length;
  const aiCount = aiIds.length;

  if (selectedCount === 0) {
    return {
      label: "No MLUs selected yet",
      color: "#9e9e9e",
      kind: "none",
    };
  }

  const intersectionCount = selectedIds.filter((id) => aiSet.has(id)).length;

  const equalSets =
    selectedCount === aiCount &&
    selectedIds.every((id) => aiSet.has(id)) &&
    aiIds.every((id) => selectedSet.has(id));

  if (equalSets && selectedCount > 0) {
    return {
      label: "AI selection only",
      color: "#fbc02d",
      kind: "ai-only",
    };
  }

  if (intersectionCount > 0 && !equalSets) {
    return {
      label: "Hybrid selection (AI + human)",
      color: "#2e7d32",
      kind: "hybrid",
    };
  }

  return {
    label: "Human selection only",
    color: "#fb8c00",
    kind: "human",
  };
}

// ---------------------------------------------------------------------
// Emoji icon for selection status
// ---------------------------------------------------------------------
function getStatusEmoji(kind) {
  switch (kind) {
    case "ai-only":
      return "🤖";
    case "hybrid":
      return "🤖 + 👤";
    case "human":
      return "👤";
    default:
      return "";
  }
}


// ---------------------------------------------------------------------
// Helper: first sentence preview for collapsed state
// ---------------------------------------------------------------------
function getFirstSentencePreview(text) {
  if (!text) return "";
  const normalized = text.trim().replace(/\s+/g, " ");
  if (!normalized) return "";

  const match = normalized.match(/([.!?])\s/);
  if (!match || match.index == null) {
    // No clear sentence boundary – show truncated whole text if long
    return normalized.length > 120
      ? normalized.slice(0, 117).trimEnd() + "…"
      : normalized;
  }

  const endIndex = match.index + 1; // include punctuation
  const firstSentence = normalized.slice(0, endIndex);
  return firstSentence.length < normalized.length
    ? firstSentence + " …"
    : firstSentence;
}

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------
export default function FrameworkCustomizer({
  label,
  codePrefix,
  mlus = [],
  selected = [],
  aiSelection = [],
  aiReasons = {},
  imageSrc,
  notes = "",
  onToggle,
  onNotesChange,
  notePlaceholder = DEFAULT_NOTE_PLACEHOLDER,
  certificationNeeded = false,
}) {

  const [open, setOpen] = React.useState(false);
  const [mlusFromBackend, setMlusFromBackend] = React.useState(null);
  const [backendError, setBackendError] = React.useState(null);

  const upperPrefix = (codePrefix || "").toUpperCase();
  const bgColor = BG_COLORS[upperPrefix] || "background.paper";
  const badgeColor = MAIN_COLORS[upperPrefix] || "#424242";

  const aiIdSet = React.useMemo(
    () => new Set(Array.isArray(aiSelection) ? aiSelection : []),
    [aiSelection]
  );


  // Fetch catalog once and filter by prefix to get full MLU metadata
  React.useEffect(() => {
    let cancelled = false;

    fetchCatalogOnce()
      .then((items) => {
        if (cancelled || !items) return;

        const filtered = items
          .filter((item) =>
            (item.id || "").toUpperCase().startsWith(`${codePrefix}-`)
          )
          .map((item) => ({
            id: item.id,
            title: item.title || "",
            module: item.module || "",
            eqf: item.eqf || "",
            outcome: item.outcome || "",
          }));

        setMlusFromBackend(filtered);
        setBackendError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setBackendError(err.message || "Failed to load catalog");
          setMlusFromBackend(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [codePrefix]);

  // Fallback to `mlus` prop if catalog unavailable
  const fallbackMlus = (mlus || []).map((m) => ({
    id: m.id || m.code || "",
    title: m.title || "",
    module: m.module || "",
    eqf: m.eqf || "",
    outcome: m.outcome || "",
  }));

  const effectiveMlus = mlusFromBackend || fallbackMlus;

  const totalSelected = selected.length;
  const status = computeStatus(selected, aiSelection);
  const statusKind = status.kind;
  const statusEmoji = getStatusEmoji(statusKind);


  // Notes state and placeholder handling
  const currentNote = notes || "";
  const isEmpty = currentNote.trim().length === 0;

  const effectivePlaceholder =
    !open && isEmpty ? COLLAPSED_SIMPLE_PLACEHOLDER : notePlaceholder;

  const maxWords = 100;

  const handleNoteChange = (arg) => {
    if (!onNotesChange) return;

    let text = "";

    // Case 1: WordCountTextField calls onChange with a plain string
    if (typeof arg === "string") {
      text = arg;
    }
    // Case 2: classic MUI TextField event
    else if (arg && typeof arg === "object" && arg.target && typeof arg.target.value === "string") {
      text = arg.target.value;
    }
    // Case 3: custom object shape, e.g. { value, wordCount }
    else if (arg && typeof arg === "object" && typeof arg.value === "string") {
      text = arg.value;
    }

    const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount <= maxWords) {
      onNotesChange(text);
    }
  };


  const collapsedPreview = getFirstSentencePreview(currentNote);

  const exampleText =
    NOTE_EXAMPLE_BY_PREFIX[upperPrefix] ||
    "Use this block to connect the suggested MLUs to your specific learner, team, and organisational context. " +
    "Mention why this combination makes sense right now and what kind of follow-up activities you will organise.";

  // Border styles based on selection status
  const borderSx = (() => {
    switch (statusKind) {
      case "hybrid":
        return {
          borderWidth: 2,
          borderStyle: "solid",
          borderColor: "#000000",
        };
      case "human":
        return {
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: "#212121",
        };
      case "none":
        // Keep default MUI outlined border; we just ensure it's grey-ish
        return {
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: "#e0e0e0",
        };
      case "ai-only":
      default:
        // For AI-only we will wrap with AiBorderWrapper; Paper border can stay minimal
        return {
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: "transparent",
        };
    }
  })();

  // Handle toggling with optional pairing (same logic as FrameworkSelector)
  const handleChipClick = (mluId) => {
    if (certificationNeeded) {
      const pairedId = getPairedId(mluId);
      const hasPair = pairedId && effectiveMlus.some((m) => m.id === pairedId);
      if (hasPair) {
        const isActive = selected.includes(mluId);
        if (isActive) {
          onToggle(mluId);
          if (selected.includes(pairedId)) onToggle(pairedId);
        } else {
          onToggle(mluId);
          if (!selected.includes(pairedId)) onToggle(pairedId);
        }
        return;
      }
    }
    onToggle(mluId);
  };

  // Build chip rendering for a single MLU
  const renderChip = (mlu) => {
    const active = selected.includes(mlu.id);
    const isAiSuggested = aiIdSet.has(mlu.id);
    const aiReason = isAiSuggested ? aiReasons[mlu.id] : null;

    const tooltipContent = (
      <Box sx={{ maxWidth: 320 }}>
        {mlu.title && (
          <Typography variant="caption" display="block">
            <strong>Title:</strong> {mlu.title}
          </Typography>
        )}
        {mlu.eqf && (
          <Typography variant="caption" display="block">
            <strong>EQF:</strong> {mlu.eqf}
          </Typography>
        )}
        {mlu.outcome && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            <strong>Learning outcome:</strong> {mlu.outcome}
          </Typography>
        )}
        {aiReason && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            <strong>Why AI suggested this:</strong> {aiReason}
          </Typography>
        )}
      </Box>
    );

    return (
      <Tooltip key={mlu.id} title={tooltipContent} arrow>
        <Chip
          clickable
          label={`${mlu.id} · ${mlu.title}`}
          onClick={() => handleChipClick(mlu.id)}
          sx={{
            m: 0.5,
            height: "auto",
            minHeight: 32,
            "& .MuiChip-label": {
              whiteSpace: "normal",
              display: "block",
              py: 0.75,
              px: 1.5,
            },
            transition: "all 0.15s ease-in-out",
            ...(active
              ? {
                fontWeight: 700,
                fontSize: "0.85rem",
                color: upperPrefix === "EID" ? "rgba(0,0,0,0.87)" : "#ffffff",
                backgroundColor: badgeColor,
                border: "2px solid",
                borderColor: badgeColor,
                boxShadow: `0px 4px 0px ${badgeColor}99, 0px 4px 8px rgba(0,0,0,0.2)`,
                transform: "translateY(-4px)",
                "&:hover": {
                  backgroundColor: badgeColor,
                  transform: "translateY(-3px)",
                  boxShadow: `0px 3px 0px ${badgeColor}99, 0px 3px 6px rgba(0,0,0,0.2)`,
                },
              }
              : {
                fontWeight: 500,
                fontSize: "0.8125rem",
                color: "text.secondary",
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
                transform: "none",
                "&:hover": {
                  backgroundColor: "action.hover",
                  borderColor: "text.primary",
                },
              }),
          }}
        />
      </Tooltip>
    );
  };

  const paperContent = (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        mb: statusKind === "ai-only" ? 0 : 2, // margin moved to wrapper in AI-only case
        backgroundColor: bgColor,
        ...borderSx,
      }}
    >
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          {label && <Typography variant="subtitle1">{label}</Typography>}

          {/* Status – always visible */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", mt: 0.5, gap: 0.5 }}
          >
            {statusEmoji && (
              <Box
                component="span"
                sx={{
                  fontSize: "1rem",
                  lineHeight: 1,
                }}
              >
                {statusEmoji}
              </Box>
            )}
            {status.label}
          </Typography>


          {/* Selected MLUs – hidden when expanded */}
          {!open && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, wordBreak: "break-word" }}
            >
              <strong>Finalized MLUs:</strong>{" "}
              {totalSelected > 0 ? selected.join(", ") : "(none)"}
            </Typography>
          )}

          {backendError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              Using fallback list (catalog error).
            </Typography>
          )}

          {/* Collapsed note preview (first sentence only) */}
          {!open && !isEmpty && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, wordBreak: "break-word" }}
            >
              <strong>Note:</strong> {collapsedPreview}
            </Typography>
          )}

          {/* Collapsed hint when no note yet */}
          {!open && isEmpty && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              <strong>Note:</strong> (no note yet – expand this block to add
              one)
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {/* Counter */}
          <Box
            sx={{
              minWidth: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: badgeColor,
              color: "#fff",
              fontSize: "0.75rem",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {totalSelected}
          </Box>

          {/* Framework image */}
          {imageSrc && (
            <Box
              component="img"
              src={imageSrc}
              alt={`${upperPrefix} framework`}
              sx={{
                width: open ? 40 : 72,
                height: open ? 40 : 72,
                borderRadius: 1,
                objectFit: "cover",
              }}
            />
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpen((o) => !o)}
            endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              textTransform: "none",
              ml: 1,
              borderRadius: 2,
              fontWeight: 600,
              alignSelf: "center",
              borderColor: "divider",
              color: "text.primary"
            }}
          >
            {open ? "Hide MLUs" : "Show MLUs"}
          </Button>
        </Box>
      </Stack>

      {/* EXPANDED CONTENT – chips */}
      {open && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1.5 }}>
            Click on an MLU to select it. Click again to deselect.
            {certificationNeeded && " 🔗 Certification mode: paired MLUs are linked. Clicking one selects both."}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {certificationNeeded ? (
              // Grouped rendering for certification mode
              groupIntoPairs(effectiveMlus).map((group) => {
                if (group.length === 2) {
                  return (
                    <Box
                      key={`pair-${group[0].id}`}
                      sx={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        border: "2px dashed",
                        borderColor: "primary.light",
                        borderRadius: 2,
                        p: 0.5,
                        m: 0.5,
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                        position: "relative",
                      }}
                    >
                      <Box sx={{
                        position: "absolute",
                        top: -10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}>
                        <LinkIcon sx={{ fontSize: 14 }} />
                      </Box>
                      {group.map((mlu) => renderChip(mlu))}
                    </Box>
                  );
                }
                return renderChip(group[0]);
              })
            ) : (
              // Default: individual rendering
              effectiveMlus.map((mlu) => renderChip(mlu))
            )}
          </Box>
        </Box>
      )}


      {/* NOTES FIELD — guided component, only visible when expanded */}
      {open && (
        <Box
          sx={{
            mt: 2,
            p: { xs: 2, sm: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Short note on goals, why these MLUs, and planned follow-ups
          </Typography>
          <WordCountTextFieldWithExample
            // label removed to avoid redundancy with the section header
            value={currentNote}
            onChange={handleNoteChange}
            placeholder={effectivePlaceholder}
            minRows={8}
            minWords={10}
            maxWords={maxWords}
            hardLimit={maxWords}
            exampleText={exampleText}
            exampleTitle="Example note for this framework"
            guidingText=""
            guidingTitle=""
          />
        </Box>
      )}
    </Paper>
  );

  // Wrap with AI border when selection is AI-only
  if (statusKind === "ai-only") {
    return (
      <AiBorderWrapper
        aiActive={true}
        animated={false}
        sx={{ borderRadius: 2, mb: 2 }}
      >
        {paperContent}
      </AiBorderWrapper>
    );
  }

  // All other cases: just return the Paper with appropriate border
  return paperContent;
}
